import { useState, useMemo } from "react";

const faqs = [
  {
    q: "Who can use the library?",
    a: "All registered students, faculty members, officers, researchers, and authorized staff of Jahangirnagar University can use the library's facilities and services.",
    cat: "access",
    icon: "👥",
  },
  {
    q: "How can I search for books?",
    a: "You can search for books and other resources through the Online Public Access Catalog (OPAC) on the library website. In-person searching is also available using the manual catalog cards at the library.",
    cat: "resources",
    icon: "🔍",
  },
  {
    q: "What types of resources are available?",
    a: "The library provides access to books, journals, theses, dissertations, e-books, online journals, databases, and other academic resources to support teaching and research.",
    cat: "resources",
    icon: "📚",
  },
  {
    q: "How do I borrow books?",
    a: "You can borrow books with a valid library membership card, in accordance with the library's circulation policies. Visit the circulation desk with your card to check out items.",
    cat: "access",
    icon: "🔖",
  },
  {
    q: "Does the library provide access to online journals and databases?",
    a: "Yes. The library subscribes to numerous online journals and international research databases to support teaching and research activities across all departments.",
    cat: "digital",
    icon: "🌐",
  },
  {
    q: "Can I access electronic resources from outside campus?",
    a: "Selected electronic resources can be accessed on campus via the university's IP address without a login. Remote access is also available for some resources, subject to licensing agreements and user authentication requirements.",
    cat: "digital",
    icon: "💻",
  },
  {
    q: "What should I do if I lose a borrowed book?",
    a: "You should immediately inform the library staff and follow the prescribed replacement or compensation procedure. Delays in reporting may result in additional penalties.",
    cat: "access",
    icon: "⚠️",
  },
  {
    q: "Does the library offer research support services?",
    a: "Yes. The library provides reference services, research assistance, database searching support, and information literacy guidance. Contact the reference desk or visit during office hours to get started.",
    cat: "services",
    icon: "🔬",
  },
  {
    q: "Is there a reading space available?",
    a: "Yes. The library offers spacious reading facilities and can accommodate more than 500 users at a time, providing a comfortable and quiet environment for study and research.",
    cat: "services",
    icon: "🪑",
  },
  {
    q: "How can I contact the library?",
    a: "You can reach us by email, phone, or through the official library website. You are also welcome to visit any of our service desks during office hours.",
    cat: "services",
    icon: "✉️",
  },
];

const CATEGORIES = [
  { key: "all", label: "All questions" },
  { key: "access", label: "Access & membership" },
  { key: "resources", label: "Resources" },
  { key: "digital", label: "Digital & online" },
  { key: "services", label: "Services" },
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .faq-root {
    font-family: 'Inter', sans-serif;
    background: #f0f5ff;
    min-height: 100vh;
    padding: 0 0 80px;
    color: #0f172a;
  }

  /* ── HEADER ── */
  .faq-header {
    background: #1a4fa0;
    padding: 56px 40px 80px;
    position: relative;
    overflow: hidden;
  }

  .faq-header::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 320px; height: 320px;
    border-radius: 50%;
    background: rgba(255,255,255,.05);
    pointer-events: none;
  }

  .faq-header::after {
    content: '';
    position: absolute;
    bottom: -80px; left: 30%;
    width: 200px; height: 200px;
    border-radius: 50%;
    background: rgba(255,255,255,.04);
    pointer-events: none;
  }

  .faq-header-inner {
    max-width: 760px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .faq-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(255,255,255,.12);
    border: 1px solid rgba(255,255,255,.18);
    color: #bdd4f8;
    font-size: .72rem;
    font-weight: 600;
    letter-spacing: .1em;
    text-transform: uppercase;
    padding: 6px 14px;
    border-radius: 999px;
    margin-bottom: 18px;
  }

  .faq-header h1 {
    font-size: 2.2rem;
    font-weight: 600;
    color: #fff;
    line-height: 1.15;
    margin-bottom: 12px;
  }

  .faq-header p {
    color: #93b8f0;
    font-size: .97rem;
    line-height: 1.65;
    max-width: 520px;
  }

  /* ── SEARCH CARD (overlaps header) ── */
  .faq-body {
    max-width: 760px;
    margin: -36px auto 0;
    padding: 0 24px;
    position: relative;
    z-index: 2;
  }

  .faq-search-card {
    background: #fff;
    border-radius: 16px;
    padding: 20px 20px 0;
    box-shadow: 0 4px 24px rgba(26,79,160,.12), 0 1px 4px rgba(0,0,0,.06);
    margin-bottom: 20px;
  }

  .faq-search-wrap {
    position: relative;
    margin-bottom: 16px;
  }

  .faq-search-wrap svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    pointer-events: none;
  }

  .faq-search-input {
    width: 100%;
    padding: 11px 14px 11px 42px;
    font-size: .9rem;
    font-family: 'Inter', sans-serif;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    background: #f8faff;
    color: #0f172a;
    outline: none;
    transition: border-color .15s, box-shadow .15s;
  }

  .faq-search-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59,130,246,.12);
    background: #fff;
  }

  .faq-search-input::placeholder { color: #94a3b8; }

  .faq-tabs {
    display: flex;
    gap: 2px;
    overflow-x: auto;
    padding-bottom: 0;
    scrollbar-width: none;
  }

  .faq-tabs::-webkit-scrollbar { display: none; }

  .faq-tab {
    flex-shrink: 0;
    background: transparent;
    border: none;
    border-bottom: 2.5px solid transparent;
    padding: 10px 14px;
    font-size: .8rem;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    color: #64748b;
    cursor: pointer;
    white-space: nowrap;
    transition: color .15s, border-color .15s;
    border-radius: 0;
  }

  .faq-tab:hover { color: #1a4fa0; }

  .faq-tab.active {
    color: #1a4fa0;
    border-bottom-color: #1a4fa0;
  }

  /* ── ACCORDION ── */
  .faq-meta {
    font-size: .75rem;
    color: #94a3b8;
    margin-bottom: 10px;
    padding: 0 2px;
  }

  .faq-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .faq-item {
    background: #fff;
    border-radius: 14px;
    border: 1.5px solid #e2e8f0;
    overflow: hidden;
    transition: border-color .15s, box-shadow .15s;
  }

  .faq-item.open {
    border-color: #93c5fd;
    box-shadow: 0 2px 16px rgba(59,130,246,.1);
  }

  .faq-question {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    cursor: pointer;
    background: transparent;
    border: none;
    width: 100%;
    text-align: left;
    font-family: 'Inter', sans-serif;
  }

  .faq-question:hover { background: #f8faff; }
  .faq-item.open .faq-question { background: #f0f7ff; }

  .faq-q-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #eff6ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    flex-shrink: 0;
    transition: background .15s;
  }

  .faq-item.open .faq-q-icon { background: #dbeafe; }

  .faq-q-text {
    flex: 1;
    font-size: .9rem;
    font-weight: 500;
    color: #1e293b;
    line-height: 1.45;
  }

  .faq-chevron {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform .2s, background .15s;
    color: #64748b;
  }

  .faq-item.open .faq-chevron {
    transform: rotate(180deg);
    background: #dbeafe;
    color: #1a4fa0;
  }

  .faq-answer {
    max-height: 0;
    overflow: hidden;
    transition: max-height .28s ease, padding .28s ease;
    padding: 0 18px 0 68px;
  }

  .faq-item.open .faq-answer {
    max-height: 300px;
    padding: 0 18px 18px 68px;
  }

  .faq-answer p {
    font-size: .875rem;
    color: #475569;
    line-height: 1.7;
  }

  .faq-divider {
    height: 1px;
    background: #e2e8f0;
    margin: 0 18px;
  }

  .faq-item.open .faq-divider { background: #bfdbfe; }

  /* ── EMPTY STATE ── */
  .faq-empty {
    text-align: center;
    padding: 48px 20px;
    background: #fff;
    border-radius: 14px;
    border: 1.5px solid #e2e8f0;
  }

  .faq-empty-icon { font-size: 2.5rem; margin-bottom: 12px; }
  .faq-empty h3 { font-size: 1rem; font-weight: 600; color: #1e293b; margin-bottom: 6px; }
  .faq-empty p { font-size: .85rem; color: #94a3b8; }

  /* ── CONTACT FOOTER ── */
  .faq-contact {
    margin-top: 24px;
    background: linear-gradient(135deg, #1a4fa0, #2563eb);
    border-radius: 14px;
    padding: 24px 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .faq-contact-text h3 {
    font-size: .95rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 4px;
  }

  .faq-contact-text p {
    font-size: .8rem;
    color: #93c5fd;
  }

  .faq-contact-btn {
    flex-shrink: 0;
    background: rgba(255,255,255,.15);
    border: 1.5px solid rgba(255,255,255,.25);
    color: #fff;
    padding: 10px 20px;
    border-radius: 10px;
    font-size: .82rem;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
    cursor: pointer;
    text-decoration: none;
    transition: background .15s;
    white-space: nowrap;
  }

  .faq-contact-btn:hover { background: rgba(255,255,255,.25); }

  @media (max-width: 600px) {
    .faq-header { padding: 40px 24px 70px; }
    .faq-header h1 { font-size: 1.6rem; }
    .faq-body { padding: 0 14px; }
    .faq-answer { padding-left: 18px !important; }
    .faq-item.open .faq-answer { padding-left: 18px !important; }
    .faq-contact { flex-direction: column; align-items: flex-start; }
  }
`;

export default function LibraryFAQ() {
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [openIndex, setOpenIndex] = useState(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return faqs.filter((f) => {
      const catMatch = activeTab === "all" || f.cat === activeTab;
      const termMatch =
        !term ||
        f.q.toLowerCase().includes(term) ||
        f.a.toLowerCase().includes(term);
      return catMatch && termMatch;
    });
  }, [activeTab, search]);

  const toggle = (i:any) => setOpenIndex(openIndex === i ? null : i);

  return (
    <>
      <style>{styles}</style>
      <div className="faq-root">
        {/* Header */}
        <div className="faq-header">
          <div className="faq-header-inner">
            <div className="faq-eyebrow">📖 University Library · JU</div>
            <h1>Frequently asked questions</h1>
            <p>
              Everything you need to know about library membership, borrowing,
              digital resources, and support services.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="faq-body">
          {/* Search + tabs card */}
          <div className="faq-search-card">
            <div className="faq-search-wrap">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="faq-search-input"
                type="search"
                placeholder="Search questions…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setOpenIndex(null); }}
                aria-label="Search frequently asked questions"
              />
            </div>
            <div className="faq-tabs" role="tablist" aria-label="Filter by category">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={activeTab === c.key}
                  className={`faq-tab${activeTab === c.key ? " active" : ""}`}
                  onClick={() => { setActiveTab(c.key); setOpenIndex(null); }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Count */}
          <div className="faq-meta">
            {filtered.length === 0
              ? "No results"
              : `${filtered.length} question${filtered.length !== 1 ? "s" : ""}`}
          </div>

          {/* Accordion */}
          {filtered.length === 0 ? (
            <div className="faq-empty">
              <div className="faq-empty-icon">🔍</div>
              <h3>No matching questions</h3>
              <p>Try a different keyword or browse all categories.</p>
            </div>
          ) : (
            <div className="faq-list" role="list">
              {filtered.map((f, i) => {
                const isOpen = openIndex === i;
                return (
                  <div
                    key={i}
                    className={`faq-item${isOpen ? " open" : ""}`}
                    role="listitem"
                  >
                    <button
                      className="faq-question"
                      onClick={() => toggle(i)}
                      aria-expanded={isOpen}
                    >
                      <span className="faq-q-icon" aria-hidden="true">{f.icon}</span>
                      <span className="faq-q-text">{f.q}</span>
                      <span className="faq-chevron" aria-hidden="true">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </span>
                    </button>
                    {isOpen && <div className="faq-divider" />}
                    <div className="faq-answer" aria-hidden={!isOpen}>
                      <p>{f.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Contact CTA */}
          <div className="faq-contact">
            <div className="faq-contact-text">
              <h3>Still have a question?</h3>
              <p>Our library team is happy to help during office hours.</p>
            </div>
            <a href="mailto:library@juniv.edu" className="faq-contact-btn">
              Contact us →
            </a>
          </div>
        </div>
      </div>
    </>
  );
}